import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Box, Button, Chip, Popover } from '@mui/material';
import { KeyboardArrowDown as ArrowDownIcon, Clear as DeleteIcon } from '@mui/icons-material';
import { MUIRichTreeView } from 'components';

export default function TreeMultiSelectDropdown({
	treeData,
	flatCategories,
	label,
	onChange,
	selectedItems,
}) {
	const [anchorEl, setAnchorEl] = useState(null);
	const [selectedIds, setSelectedIds] = useState([]);
	const [disabledIds, setDisabledIds] = useState([]);

	// Converts tree structure into a flat object keyed by id
	const flatTree = useMemo(() => {
		const flat = {};
		const walk = (nodes) => {
			nodes.forEach((node) => {
				const { id, name, children = [] } = node;
				flat[id] = {
					id: String(id),
					name: name,
					children: children.map((c) => ({ id: String(c.id) })),
				};
				if (children.length) walk(children);
			});
		};
		walk(treeData);
		return flat;
	}, [treeData]);

	const convertToNameTree = (nodes) => {
		return nodes.map(({ id, name, children = [] }) => ({
			id: String(id),
			name: name,
			children: convertToNameTree(children),
		}));
	};

	const handleDelete = (id, item) => {
		const newSelected = selectedIds.filter((sid) => sid !== id);
		setSelectedIds(newSelected);
		setDisabledIds((prev) =>
			prev.filter((value) => !item.children?.find((child) => child.id === value))
		);
		if (onChange) onChange(formatSelectedObjects(newSelected));
	};
	const handlePopoverClose = () => {
		setAnchorEl(null);
		if (onChange) onChange(formatSelectedObjects(selectedIds));
	};

	const formattedTreeData = convertToNameTree(treeData);
	const formatSelectedObjects = (ids) =>
		ids.map((id) => ({
			id,
			name: flatTree[id]?.name || '',
		}));

	React.useEffect(() => {
		if (selectedItems?.length) {
			setSelectedIds(selectedItems.map((item) => String(item.id)));
		}
	}, [selectedItems]);
	return (
		<Box
			sx={{
				display: 'flex',
				flexDirection: 'column',
				width: '100%',
			}}
		>
			<Button
				fullWidth
				size="small"
				variant="outlined"
				// color="primary"
				endIcon={
					selectedIds.length === 0 ? (
						<ArrowDownIcon />
					) : (
						<Box
							component="span"
							onClick={(e) => {
								e.stopPropagation();
								setSelectedIds([]);
								setDisabledIds([]);
								onChange([]);
							}}
							sx={{
								display: 'flex',
								alignItems: 'center',
								cursor: 'pointer',
								pl: 0.5,
							}}
						>
							<DeleteIcon fontSize="small" />
						</Box>
					)
				}
				onClick={(e) => setAnchorEl(e.currentTarget)}
				sx={{
					display: 'flex',
					justifyContent: 'space-between',
					alignItems: 'center',
					p: 1,
					gap: 1,
					flexWrap: 'wrap',
					color: '#bdbdbd',
					borderColor: 'rgba(0, 0, 0, 0.23)',
					border: '1px solid',
					'&:hover': {
						borderColor: 'rgba(0, 0, 0, 1)',
						backgroundColor: 'transparent',
					},
				}}
			>
				{selectedIds.length === 0 ? (
					label
				) : (
					<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, flex: 1 }}>
						{selectedIds.map((id) => {
							const item = flatCategories[id];
							return (
								<Chip
									key={id}
									label={item?.name || id}
									size="small"
									onDelete={() => handleDelete(id, item)}
									sx={{ maxWidth: 100 }}
								/>
							);
						})}
					</Box>
				)}
			</Button>

			<Popover
				anchorEl={anchorEl}
				open={Boolean(anchorEl)}
				onClose={handlePopoverClose}
				anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
				transformOrigin={{ vertical: 'top', horizontal: 'left' }}
			>
				<Box sx={{ minWidth: 300, maxHeight: 400, p: 1 }}>
					<MUIRichTreeView
						treeItems={formattedTreeData}
						parentChildrenSelection={false}
						disableChildrenIfParentisSelected={true}
						enableMultiSelection={true}
						enableSearch={true}
						selectedItemIds={selectedIds}
						setSelectedItemIds={setSelectedIds}
						disabledItemIds={disabledIds}
						setDisabledItemIds={setDisabledIds}
						isAnyChildrenSelected={(itemId) => {
							return selectedIds.some((selectedId) => {
								return (
									itemId !== selectedId &&
									flatCategories[itemId].children.find((child) => child.id === selectedId)
								);
							});
						}}
						getItemId={(item) => item.id}
						getItemLabel={(item) => item.name}
					/>
				</Box>
			</Popover>
		</Box>
	);
}

TreeMultiSelectDropdown.propTypes = {
	treeData: PropTypes.arrayOf(
		PropTypes.shape({
			id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
			name: PropTypes.string.isRequired,
			children: PropTypes.array,
		})
	).isRequired,
	flatCategories: PropTypes.objectOf(
		PropTypes.shape({
			id: PropTypes.string.isRequired,
			name: PropTypes.string.isRequired,
			parents: PropTypes.array,
			children: PropTypes.array,
		})
	),
	label: PropTypes.string,
	onChange: PropTypes.func,
	selectedItems: PropTypes.arrayOf(
		PropTypes.shape({
			id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
			name: PropTypes.string.isRequired,
		})
	),
};

TreeMultiSelectDropdown.defaultProps = {
	label: 'Select Items',
	onChange: () => {},
};
